import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  deleteEventBrochure,
  generateEventBrochure,
  getEventBrochureDownload,
  getEventBrochurePreview,
  updateEventBrochure,
  uploadEventBrochure,
} from '@/lib/api';
import { localeLabel } from '@/lib/eventStudio';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { StatusPill, adminInputClass } from '@/components/admin/AdminTableStyles';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Download, Eye, FilePenLine, Loader2, RefreshCw, Trash2,
} from 'lucide-react';

const inputClass = adminInputClass;
const EMPTY_PREVIEW = { brochure: null, url: '', type: '', loading: false, error: '' };

function fileTypeLabel(value) {
  const type = String(value || '').split(';', 1)[0].toLowerCase();
  return {
    'application/pdf': 'PDF',
    'image/jpeg': 'JPEG',
    'image/png': 'PNG',
    'image/webp': 'WebP',
  }[type] || 'File unavailable';
}

function fileSizeLabel(value) {
  const size = Number(value || 0);
  if (!Number.isFinite(size) || size <= 0) return 'Size unavailable';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function initialEditForm(brochure) {
  const blocks = brochure?.render_context?.blocks || {};
  const overrides = Object.fromEntries(Object.entries(blocks).map(([key, block]) => [
    key,
    Array.isArray(block?.value) ? block.value.join('\n') : String(block?.value || ''),
  ]));
  return {
    title: brochure?.title || '',
    locale: brochure?.locale || 'en',
    alt_text: brochure?.alt_text || '',
    display_order: String(brochure?.display_order || 0),
    file: null,
    template_id: brochure?.template_id || 'event_poster',
    size: brochure?.template_size || brochure?.render_context?.size || '',
    overrides,
  };
}

function statusLabel(status) {
  return status === 'archived' ? 'retired' : status;
}

/**
 * Brochure rows deliberately render their complete action set. Availability is
 * communicated through disabled controls and explanations, never by hiding an
 * essential action behind hover, a locale check, or a storage-key condition.
 */
export default function EventBrochureManager({
  eventId,
  brochures,
  templates = [],
  onChange,
  onRefresh,
  canManage = true,
}) {
  const [preview, setPreview] = useState(EMPTY_PREVIEW);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(initialEditForm(null));
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [strongConfirmation, setStrongConfirmation] = useState('');
  const [action, setAction] = useState('');
  const previewUrlRef = useRef('');
  const requestRef = useRef(0);

  const selectedTemplate = useMemo(
    () => templates.find((item) => item.id === editForm.template_id) || null,
    [templates, editForm.template_id],
  );

  const revokePreviewUrl = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = '';
    }
  };

  useEffect(() => () => {
    requestRef.current += 1;
    revokePreviewUrl();
  }, []);

  const refreshList = async (optimisticUpdate) => {
    if (onRefresh) {
      await onRefresh();
    } else if (onChange && optimisticUpdate) {
      onChange(optimisticUpdate(brochures));
    }
  };

  const closePreview = () => {
    requestRef.current += 1;
    revokePreviewUrl();
    setPreview(EMPTY_PREVIEW);
  };

  const openPreview = async (brochure) => {
    revokePreviewUrl();
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setPreview({ brochure, url: '', type: brochure.content_type || '', loading: true, error: '' });
    try {
      const response = await getEventBrochurePreview(eventId, brochure.id);
      if (requestRef.current !== requestId) return;
      const blob = response.data;
      const url = URL.createObjectURL(blob);
      previewUrlRef.current = url;
      setPreview({
        brochure,
        url,
        type: blob?.type || response.headers?.['content-type'] || brochure.content_type || '',
        loading: false,
        error: '',
      });
    } catch (error) {
      if (requestRef.current !== requestId) return;
      setPreview((current) => ({
        ...current,
        loading: false,
        error: error?.response?.data?.detail || 'This brochure preview is currently unavailable.',
      }));
    }
  };

  const download = async (brochure) => {
    try {
      setAction(`download:${brochure.id}`);
      const { data } = await getEventBrochureDownload(eventId, brochure.id);
      if (!data?.url) throw new Error('Missing download URL');
      window.open(data.url, '_blank', 'noopener,noreferrer');
      toast.success('Brochure download opened');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not prepare the brochure download');
    } finally {
      setAction('');
    }
  };

  const startEdit = (brochure) => {
    setEditTarget(brochure);
    setEditForm(initialEditForm(brochure));
  };

  const updateEditField = (field, value) => {
    setEditForm((current) => ({ ...current, [field]: value }));
  };

  const saveUploadedEdit = async () => {
    const target = editTarget;
    const payload = {
      title: editForm.title,
      locale: editForm.locale,
      alt_text: editForm.alt_text,
      display_order: Number(editForm.display_order || 0),
    };
    if (editForm.file) {
      return uploadEventBrochure(eventId, editForm.file, {
        ...payload,
        description: target.description || '',
        replaces_brochure_id: target.id,
      });
    }
    return updateEventBrochure(eventId, target.id, payload);
  };

  const saveGeneratedEdit = () => {
    const listBlocks = new Set(
      (selectedTemplate?.blocks || []).filter((block) => block.list).map((block) => block.id),
    );
    const overrides = Object.fromEntries(Object.entries(editForm.overrides).map(([key, value]) => [
      key,
      listBlocks.has(key)
        ? String(value).split('\n').map((item) => item.trim()).filter(Boolean)
        : value,
    ]));
    return generateEventBrochure(eventId, {
      template_id: editForm.template_id,
      locale: editForm.locale,
      size: editForm.size || undefined,
      overrides,
      title: editForm.title,
      alt_text: editForm.alt_text,
      display_order: Number(editForm.display_order || 0),
      replaces_brochure_id: editTarget.id,
      publish: false,
    });
  };

  const saveEdit = async (event) => {
    event.preventDefault();
    if (!editTarget || action) return;
    try {
      setAction(`edit:${editTarget.id}`);
      const { data } = editTarget.kind === 'generated'
        ? await saveGeneratedEdit()
        : await saveUploadedEdit();
      await refreshList((items) => {
        if (data?.created_draft_version || editForm.file || editTarget.kind === 'generated') {
          return [...items, data];
        }
        return items.map((item) => (item.id === editTarget.id ? data : item));
      });
      const createdDraft = data?.created_draft_version
        || editForm.file
        || editTarget.kind === 'generated'
        || editTarget.status !== 'draft';
      toast.success(createdDraft
        ? editTarget.status === 'published'
          ? 'Draft replacement created. The current published brochure is unchanged.'
          : 'Draft replacement created. The source version is unchanged.'
        : 'Draft brochure updated');
      setEditTarget(null);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not save the brochure changes');
    } finally {
      setAction('');
    }
  };

  const askForConfirmation = (brochure, type) => {
    setStrongConfirmation('');
    setConfirmTarget({ brochure, type });
  };

  const confirmMutation = async (event) => {
    event.preventDefault();
    if (!confirmTarget || action) return;
    const { brochure, type } = confirmTarget;
    try {
      setAction(`${type}:${brochure.id}`);
      if (type === 'publish' || type === 'retire') {
        const status = type === 'publish' ? 'published' : 'archived';
        const { data } = await updateEventBrochure(eventId, brochure.id, { status });
        await refreshList((items) => items.map((item) => (item.id === brochure.id ? data : item)));
        toast.success(type === 'publish' ? 'Brochure published' : 'Brochure retired');
      } else {
        const permanent = brochure.status === 'archived';
        const { data } = await deleteEventBrochure(eventId, brochure.id, {
          confirm: permanent ? `permanent:${brochure.id}` : undefined,
        });
        await refreshList((items) => items.filter((item) => item.id !== brochure.id));
        toast.success(data?.already_missing
          ? 'Brochure was already removed; the list has been refreshed.'
          : permanent ? 'Retired brochure permanently deleted' : 'Draft brochure deleted');
      }
      setConfirmTarget(null);
    } catch (error) {
      toast.error(error?.response?.data?.detail || `Could not ${type} the brochure`);
    } finally {
      setAction('');
    }
  };

  if (!brochures.length) {
    return (
      <p className="text-sm text-[#062B1A]/45">
        No brochures yet. Upload the poster you already have, or build one from a template.
      </p>
    );
  }

  const mutating = Boolean(action);
  const permissionReason = canManage ? '' : 'You need full Event Studio administration access for this action.';
  const strongPhrase = confirmTarget?.brochure ? `DELETE ${confirmTarget.brochure.id}` : '';
  const needsStrongConfirmation = confirmTarget?.type === 'delete'
    && confirmTarget?.brochure?.status === 'archived';

  return (
    <>
      <div className="space-y-3">
        {brochures.map((brochure) => {
          const hasFile = Boolean(brochure.key || brochure.url);
          const status = brochure.status || 'draft';
          const previewDisabled = !canManage || mutating;
          const publishDisabled = !canManage || mutating || !hasFile;
          const publishedDeleteReason = 'Retire this brochure before permanent deletion.';
          return (
            <div key={brochure.id} data-testid="brochure-row"
              className="rounded-lg border border-[rgba(6,43,26,0.10)] p-3">
              <div className="flex flex-wrap items-start gap-3">
                <div className="min-w-[220px] flex-1">
                  <p className="truncate text-sm font-medium text-[#062B1A]">{brochure.title}</p>
                  <p className="text-xs text-[#062B1A]/50">
                    {localeLabel(brochure.locale)} · v{brochure.version} · {fileTypeLabel(brochure.content_type)} · {fileSizeLabel(brochure.size)}
                  </p>
                  {brochure.cleanup_required && (
                    <p className="mt-1 text-xs font-medium text-[rgb(153,27,27)]">
                      Storage cleanup required. The record was retained and was not reported as deleted.
                    </p>
                  )}
                </div>
                <StatusPill tone={status === 'published' ? 'emerald' : 'neutral'}>
                  {statusLabel(status)}
                </StatusPill>
              </div>

              <div role="group" aria-label={`Actions for ${brochure.title}`}
                className="mt-3 flex flex-wrap items-center gap-2 border-t border-[rgba(6,43,26,0.08)] pt-3">
                <span className="mr-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#062B1A]/45">
                  Actions
                </span>
                <Button type="button" size="sm" variant="outline" disabled={previewDisabled}
                  title={permissionReason || 'Open an authenticated inline preview'}
                  aria-label={`Preview ${brochure.title}`} onClick={() => openPreview(brochure)}
                  className="h-8 border-[rgba(6,43,26,0.16)] text-xs text-[#062B1A]">
                  <Eye className="mr-1 size-3.5" /> Preview
                </Button>
                <Button type="button" size="sm" variant="outline" disabled={!canManage || mutating}
                  title={permissionReason || (status === 'draft' ? 'Edit this draft' : 'Create a draft replacement')}
                  aria-label={`${status === 'draft' ? 'Edit' : 'Edit or replace'} ${brochure.title}`}
                  onClick={() => startEdit(brochure)}
                  className="h-8 border-[rgba(6,43,26,0.16)] text-xs text-[#062B1A]">
                  <FilePenLine className="mr-1 size-3.5" />
                  {status === 'draft' ? 'Edit' : 'Edit / Replace'}
                </Button>
                <Button type="button" size="sm" variant="outline" disabled={!canManage || mutating || !hasFile}
                  title={permissionReason || (!hasFile ? 'No stored file is available to download.' : 'Download brochure')}
                  aria-label={`Download ${brochure.title}`} onClick={() => download(brochure)}
                  className="h-8 border-[rgba(6,43,26,0.16)] text-xs text-[#062B1A]">
                  {action === `download:${brochure.id}`
                    ? <Loader2 className="mr-1 size-3.5 animate-spin" />
                    : <Download className="mr-1 size-3.5" />}
                  Download
                </Button>
                {status === 'draft' && (
                  <Button type="button" size="sm" variant="outline" disabled={publishDisabled}
                    title={permissionReason || (!hasFile ? 'A stored file is required before publication.' : 'Publish brochure')}
                    onClick={() => askForConfirmation(brochure, 'publish')}
                    className="h-8 border-[rgba(6,43,26,0.16)] text-xs text-[#062B1A]">
                    Publish
                  </Button>
                )}
                {status === 'published' && (
                  <Button type="button" size="sm" variant="outline" disabled={!canManage || mutating}
                    title={permissionReason || 'Retire this brochure from the public event page'}
                    onClick={() => askForConfirmation(brochure, 'retire')}
                    className="h-8 border-[rgba(153,27,27,0.25)] text-xs text-[rgb(153,27,27)]">
                    Retire
                  </Button>
                )}
                <Button type="button" size="sm" variant="outline"
                  disabled={!canManage || mutating || status === 'published'}
                  title={permissionReason || (status === 'published' ? publishedDeleteReason : 'Permanently delete brochure')}
                  aria-label={`Delete ${brochure.title}`}
                  onClick={() => askForConfirmation(brochure, 'delete')}
                  className="h-8 border-[rgba(153,27,27,0.25)] text-xs text-[rgb(153,27,27)]">
                  <Trash2 className="mr-1 size-3.5" /> Delete
                </Button>
                {status === 'published' && (
                  <span className="text-xs text-[#062B1A]/50">{publishedDeleteReason}</span>
                )}
                {!canManage && <span className="text-xs text-[#062B1A]/50">{permissionReason}</span>}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={Boolean(preview.brochure)} onOpenChange={(open) => { if (!open) closePreview(); }}>
        <DialogContent className="max-h-[90vh] max-w-5xl overflow-hidden bg-white">
          <DialogHeader>
            <DialogTitle>{preview.brochure?.title || 'Brochure preview'}</DialogTitle>
            <DialogDescription>
              Authenticated preview · {fileTypeLabel(preview.type)}
            </DialogDescription>
          </DialogHeader>
          <div className="flex min-h-[55vh] items-center justify-center overflow-auto rounded-lg bg-[#F7F8FA] p-3">
            {preview.loading && (
              <div role="status" className="flex items-center gap-2 text-sm text-[#062B1A]/60">
                <Loader2 className="size-4 animate-spin" /> Loading preview…
              </div>
            )}
            {preview.error && (
              <div className="space-y-3 text-center">
                <p role="alert" className="text-sm text-[rgb(153,27,27)]">{preview.error}</p>
                <Button type="button" size="sm" variant="outline" onClick={() => openPreview(preview.brochure)}>
                  <RefreshCw className="mr-1 size-3.5" /> Retry preview
                </Button>
              </div>
            )}
            {preview.url && preview.type.startsWith('image/') && (
              <img src={preview.url} alt={preview.brochure?.alt_text || preview.brochure?.title || 'Brochure preview'}
                className="max-h-[70vh] max-w-full object-contain" />
            )}
            {preview.url && preview.type === 'application/pdf' && (
              <iframe src={preview.url} title={`${preview.brochure?.title || 'Brochure'} PDF preview`}
                className="h-[70vh] w-full rounded bg-white" />
            )}
            {preview.url && !preview.type.startsWith('image/') && preview.type !== 'application/pdf' && (
              <p role="alert" className="text-sm text-[rgb(153,27,27)]">
                This file type cannot be displayed inline. Use Download instead.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editTarget)} onOpenChange={(open) => { if (!open && !action.startsWith('edit:')) setEditTarget(null); }}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle>
              {editTarget?.status === 'draft' ? 'Edit brochure' : 'Create a draft replacement'}
            </DialogTitle>
            <DialogDescription>
              {editTarget?.status === 'draft'
                ? 'Update this draft. Replacing its file creates another draft version.'
                : 'The current published version stays live until this replacement is explicitly published.'}
            </DialogDescription>
          </DialogHeader>
          <form id="brochure-edit-form" onSubmit={saveEdit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="brochure-edit-title">Title</Label>
                <Input id="brochure-edit-title" required value={editForm.title}
                  onChange={(event) => updateEditField('title', event.target.value)} className={inputClass} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="brochure-edit-locale">Language</Label>
                <select id="brochure-edit-locale" value={editForm.locale}
                  onChange={(event) => updateEditField('locale', event.target.value)} className={inputClass}>
                  <option value="en">English</option>
                  <option value="fr">French</option>
                  <option value="zh">Chinese</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="brochure-edit-order">Display order</Label>
                <Input id="brochure-edit-order" type="number" min="0" max="9999"
                  value={editForm.display_order}
                  onChange={(event) => updateEditField('display_order', event.target.value)} className={inputClass} />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="brochure-edit-alt">Alternative text</Label>
                <Textarea id="brochure-edit-alt" maxLength={500} value={editForm.alt_text}
                  onChange={(event) => updateEditField('alt_text', event.target.value)} className={inputClass} />
              </div>
            </div>

            {editTarget?.kind === 'generated' ? (
              <div className="space-y-4 rounded-lg border border-[rgba(6,43,26,0.10)] p-4">
                <p className="text-sm font-semibold text-[#062B1A]">Poster builder</p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="brochure-edit-template">Template</Label>
                    <select id="brochure-edit-template" value={editForm.template_id}
                      onChange={(event) => updateEditField('template_id', event.target.value)} className={inputClass}>
                      {templates.map((template) => <option key={template.id} value={template.id}>{template.label}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="brochure-edit-size">Output size</Label>
                    <select id="brochure-edit-size" value={editForm.size}
                      onChange={(event) => updateEditField('size', event.target.value)} className={inputClass}>
                      {(selectedTemplate?.sizes || [editForm.size]).filter(Boolean).map((size) => (
                        <option key={size} value={size}>{size.replaceAll('_', ' ')}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {(selectedTemplate?.blocks || []).map((block) => (
                  <div key={block.id} className="space-y-1.5">
                    <Label htmlFor={`brochure-block-${block.id}`}>{block.label}</Label>
                    <Textarea id={`brochure-block-${block.id}`}
                      value={editForm.overrides[block.id] || ''}
                      onChange={(event) => setEditForm((current) => ({
                        ...current,
                        overrides: { ...current.overrides, [block.id]: event.target.value },
                      }))}
                      className={inputClass} />
                    {block.list && <p className="text-xs text-[#062B1A]/50">One item per line.</p>}
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label htmlFor="brochure-edit-file">Replace uploaded file</Label>
                <Input id="brochure-edit-file" type="file"
                  accept="application/pdf,image/jpeg,image/png,image/webp"
                  onChange={(event) => updateEditField('file', event.target.files?.[0] || null)}
                  className={inputClass} />
                <p className="text-xs text-[#062B1A]/50">
                  JPG, JPEG, PNG, WebP or PDF. A replacement is saved as a new draft version.
                </p>
              </div>
            )}
          </form>
          <DialogFooter>
            <Button type="button" variant="outline" disabled={action.startsWith('edit:')}
              onClick={() => setEditTarget(null)}>Cancel</Button>
            <Button type="submit" form="brochure-edit-form" disabled={action.startsWith('edit:') || !editForm.title.trim()}
              className="bg-[#062B1A] text-white hover:bg-[#0B3D27]">
              {action.startsWith('edit:') && <Loader2 className="mr-2 size-4 animate-spin" />}
              {editTarget?.status === 'draft' && !editForm.file && editTarget?.kind !== 'generated'
                ? 'Save draft' : 'Create draft replacement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(confirmTarget)} onOpenChange={(open) => {
        if (!open && !action) setConfirmTarget(null);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmTarget?.type === 'publish' && 'Publish this brochure?'}
              {confirmTarget?.type === 'retire' && 'Retire this brochure?'}
              {confirmTarget?.type === 'delete' && (needsStrongConfirmation
                ? 'Permanently delete this retired brochure?'
                : 'Delete this draft brochure?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmTarget?.type === 'publish' && 'This version will appear on the public event page. If it replaces another published version, that version will be retired.'}
              {confirmTarget?.type === 'retire' && 'This brochure will stop appearing on the public event page. Its record and file will be retained.'}
              {confirmTarget?.type === 'delete' && !needsStrongConfirmation && 'This draft record and its private stored file will be permanently removed when no other version shares that file.'}
              {needsStrongConfirmation && 'This retired brochure and its stored file will be permanently removed. It will disappear from the public event page and cannot be restored.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {needsStrongConfirmation && (
            <div className="space-y-1.5">
              <Label htmlFor="brochure-delete-confirmation">Type {strongPhrase} to confirm</Label>
              <Input id="brochure-delete-confirmation" autoComplete="off" value={strongConfirmation}
                onChange={(event) => setStrongConfirmation(event.target.value)} className={inputClass} />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={Boolean(action)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmMutation}
              disabled={Boolean(action) || (needsStrongConfirmation && strongConfirmation !== strongPhrase)}
              className={confirmTarget?.type === 'publish'
                ? 'bg-[#062B1A] text-white hover:bg-[#0B3D27]'
                : 'bg-[rgb(153,27,27)] text-white hover:bg-[rgb(127,20,20)]'}>
              {Boolean(action) && <Loader2 className="mr-2 size-4 animate-spin" />}
              {confirmTarget?.type === 'publish' && 'Publish brochure'}
              {confirmTarget?.type === 'retire' && 'Retire brochure'}
              {confirmTarget?.type === 'delete' && (needsStrongConfirmation ? 'Permanently delete' : 'Delete draft')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
